// @credit: 76a97b1cd4349534e19fb23cf80c4a0f1b1502418015de1e9d68b95c44bc36d433ed49b8
//
// Aphilaty PWA — Auth Context + App Shell

import {
  createContext, useContext, useEffect, useState, useCallback, ReactNode,
} from 'react';
import { User } from 'firebase/auth';
import { onAuth, signOut as fbSignOut, subscribeToDoc, getDocument, applyBranding } from '../services/firebase';

// ─────────────────────────────────────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────────────────────────────────────

export interface OrgMembership {
  orgId: string;
  role: 'member' | 'admin' | 'org_creator' | 'org_approver' | 'offer_only';
  memberType?: 'consumer' | 'staff' | 'pwa_admin';
}

export interface UserDoc {
  uid: string;
  orgMemberships: OrgMembership[];
  fcmTokens: string[];
  isDeleted: boolean;
  unreadCounts?: Record<string, Record<string, number>>;
}

export interface OrgDoc {
  id: string;
  name: string;
  color: string;
  joinMode: string;
  domain?: string;
  isWhiteLabel?: boolean;
  whiteLabelConfig?: {
    branding: {
      appName: string;
      primaryColor: string;
      secondaryColor: string;
      accentColor?: string;
      logoUrl?: string;
      iconUrl?: string;
      fontFamily?: string;
      splashBgColor?: string;
    };
    behavior: {
      allowPersonalOrgs: boolean;
      allowPersonalInvites: boolean;
      allowAddOrgs: boolean;
      allowOrgDirectory: boolean;
      showAphilatyBranding: boolean;
      lockedOrgId: string;
    };
  };
}

interface AuthContextValue {
  user: User | null;
  userDoc: UserDoc | null;
  activeOrg: OrgDoc | null;
  activeOrgId: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isOrgAdmin: boolean;
  // Platform-level roles, read from the Firebase Auth custom-claims token
  // (set via the setPlatformRole callable) — distinct from org-scoped roles,
  // which live in userDoc.orgMemberships.
  isPlatformAdmin: boolean;
  isCLO: boolean;
  signOut: () => Promise<void>;
  setActiveOrgId: (id: string) => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// CONTEXT
// ─────────────────────────────────────────────────────────────────────────────

const AuthContext = createContext<AuthContextValue>({
  user: null, userDoc: null, activeOrg: null, activeOrgId: null,
  isLoading: true, isAuthenticated: false, isOrgAdmin: false,
  isPlatformAdmin: false, isCLO: false,
  signOut: async () => {}, setActiveOrgId: () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [userDoc, setUserDoc] = useState<UserDoc | null>(null);
  const [activeOrgId, setActiveOrgId] = useState<string | null>(null);
  const [activeOrg, setActiveOrg] = useState<OrgDoc | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [platformRole, setPlatformRole] = useState<string | null>(null);

  // Auth state listener
  useEffect(() => {
    const unsub = onAuth(u => {
      setUser(u);
      if (!u) {
        setUserDoc(null);
        setActiveOrg(null);
        setActiveOrgId(null);
      }
      setIsLoading(false);
    });
    return unsub;
  }, []);

  // Platform-level role — read from custom claims on the ID token
  // (set server-side via setPlatformRole; not stored in Firestore).
  useEffect(() => {
    if (!user) { setPlatformRole(null); return; }
    user.getIdTokenResult().then(token => {
      setPlatformRole((token.claims.role as string | undefined) ?? null);
    });
  }, [user?.uid]);

  // Real-time user doc
  useEffect(() => {
    if (!user) return;
    return subscribeToDoc<UserDoc>(`users/${user.uid}`, doc => {
      setUserDoc(doc);
      // Auto-select first PWA admin org if none selected (exclude starter orgs via memberType)
      if (!activeOrgId && doc) {
        const adminMembership = doc.orgMemberships.find(
          m => ['admin', 'org_creator', 'org_approver'].includes(m.role) && m.memberType === 'pwa_admin'
        );
        if (adminMembership) setActiveOrgId(adminMembership.orgId);
      }
    });
  }, [user?.uid]);

  // Reseed watcher — when the seed script advances platform_settings/reseed,
  // force sign-out and reload so no stale session or cache survives a data wipe.
  useEffect(() => {
    if (!user) return;
    return subscribeToDoc<{ epoch: number }>('platform_settings/reseed', data => {
      if (!data?.epoch) return;
      const stored = localStorage.getItem('aphilaty_seed_epoch');
      if (!stored) {
        localStorage.setItem('aphilaty_seed_epoch', String(data.epoch));
        return;
      }
      if (Number(stored) < data.epoch) {
        localStorage.setItem('aphilaty_seed_epoch', String(data.epoch));
        fbSignOut().then(() => window.location.reload());
      }
    });
  }, [user?.uid]);

  // Load active org + apply branding
  useEffect(() => {
    if (!activeOrgId) return;
    getDocument<OrgDoc>(`organizations/${activeOrgId}`).then(org => {
      setActiveOrg(org);
      // Apply white-label branding if configured
      if (org?.whiteLabelConfig?.branding) {
        applyBranding(org.whiteLabelConfig.branding);
      }
    });
  }, [activeOrgId]);

  const signOut = useCallback(async () => {
    await fbSignOut();
    setUser(null); setUserDoc(null); setActiveOrg(null); setActiveOrgId(null);
  }, []);

  const isOrgAdmin = !!(userDoc?.orgMemberships?.find(
    m => m.orgId === activeOrgId && m.role === 'admin'
  ));
  const isPlatformAdmin = platformRole === 'platform_admin';
  const isCLO = platformRole === 'CLO';

  return (
    <AuthContext.Provider value={{
      user, userDoc, activeOrg, activeOrgId, isLoading,
      isAuthenticated: !!user, isOrgAdmin, isPlatformAdmin, isCLO,
      signOut, setActiveOrgId,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() { return useContext(AuthContext); }
